/// <reference path='../../../typings/browser.d.ts' />

namespace Application.Controllers {
    'use strict';
    import IDeviceCredential = Application.Models.IDeviceCredential;

    export interface IAclModalScope extends ng.IScope {
        entity: Models.IEntity;
        aclEntries: Models.IAclEntries;
        deviceCredentials: Models.IDeviceCredential[];
        showform: boolean;
        showformEdit: boolean;
        groups: Models.IUserGroup[];
        selected: {
            group: Models.IUserGroup;
        };
        modalClose: () => void;
        saveACl: () => void;
        toId: (href: string) => number;
        addGroup: () => void;
        removeEntry: (aclEntry: Models.AclEntry) => void;
        saveCredential: (deviceCredential: Models.IDeviceCredential) => void;
        togglePasswordVisibility: (credential: Models.IDeviceCredential) => void;
        toggleSlaveEntry: (masterType: string, aclEntry: Models.AclEntry) => void;
        valueChanged: (credential: Models.IDeviceCredential) => void;
        
        showWrite: boolean;
        showGui: boolean;
        showGroups: boolean;
    }

    /**
     * AclModalController class
     */
    export class AclModalController {
        public static templateURL: string = 'decorators/modals/acl-modal.html';
        public static ID: string = '#id-acl-modal';
        public static $name: string = 'aclModalController';
        public static $inject: string[] = ['$scope', 'close', '$interval', '$timeout',
                                           'entity', 'entityTypeName',
                                           'aclService',
                                           'userGroupService',
                                           'deviceCredentialsService'];

        private $scope: IAclModalScope;
        private $interval: ng.IIntervalService;
        private close: (parameters: ngModal.ICloseParameters) => void;

        private static ALARM_CONFIG_CLASS: string = "io.epiclabs.flex.api.AlarmConfig";

        private aclEntryTypeTranslations: { [id: string]: string; } = {
            'OrganizationUsers': 'All users',
            'OrganizationAdmins': 'Admins',
            'Others': 'Regular users',
            'Role': '',
        };

        private aclEntryRoleTranslations: {[role: string]: string;} = {
            'ORGANIZATION_ADMIN': 'VNO admins',
        };

        /**
         * Constructor
         */
        constructor($scope: IAclModalScope,
                    close: (parameters: ngModal.ICloseParameters) => void,
                    $interval: ng.IIntervalService,
                    $timeout: ng.ITimeoutService,
                    entity: Models.IEntity,
                    entityTypeName: string,
                    aclService: Services.AclService,
                    userGroupService: Services.UserGroupService,
                    deviceCredentialsService: Services.DeviceCredentialsService) {
            this.$scope = $scope;
            this.$interval = $interval;
            this.$scope.entity = entity;
            this.$scope.showform = false;
            this.$scope.showformEdit = false;
            this.$scope.selected = {
                group: undefined,
            };

            $timeout(() => {
                 window.altair_admin_common_init();
            }, 500);

            aclService.getAclsFor(entity, entityTypeName).then((acls: Models.IAclEntries) => {
                this.translateEntries(acls);
                this.setBooleans(acls);
                this.$scope.aclEntries = acls;
                this.loadGroups($scope, userGroupService);
            });

            deviceCredentialsService.getDeviceCredentialsFor(entity).then((credentials: IDeviceCredential[]) => {
                for (let credential of credentials) {
                    credential.typeHtml = credential.type === 'password' ? 'password' : 'text';
                    credential.allowSave = false;
                }
                this.$scope.deviceCredentials = credentials;
            });

            this.close = close;

            this.$scope.modalClose = (): void => {
                this.close(undefined);
            };

            this.$scope.saveACl = (): void => {
                aclService.saveAcl(this.$scope.aclEntries.aclEntries).then((acl: Models.IAclEntries) => {
                    Common.Utils.alertOnSuccess('Permissions saved correctly');
                });
            };

            this.$scope.toId = (href: string): number => {
                return Common.Utils.idFromHref(href);
            };

            this.$scope.valueChanged = (credential: Models.IDeviceCredential) => {
                credential.allowSave = credential.value !== '' || credential.type === 'clear';
            };

            this.$scope.$on('$destroy', () => {
                // do something
            });

            this.$scope.addGroup = (): void => {
                let groupAclEntry: Models.AclEntry = new Models.AclEntry();
                groupAclEntry.group = this.$scope.selected.group.id as number;
                groupAclEntry.groupName = this.$scope.selected.group.name;
                groupAclEntry.identityClass = this.$scope.aclEntries.identityClass;
                groupAclEntry.identityId = this.$scope.aclEntries.identityId;
                groupAclEntry.type = 'Group';
                groupAclEntry.canAccessGui =
                    this.$scope.aclEntries.aclEntries[0].canAccessGui === null ? null : false; // tslint:disable-line:no-null-keyword
                this.$scope.aclEntries.aclEntries.push(groupAclEntry);
                let index: number = this.$scope.groups.indexOf(this.$scope.selected.group);
                this.$scope.groups.splice(index, 1);
                if (this.$scope.groups.length > 0) {
                    this.$scope.selected.group = this.$scope.groups[0];
                } else {
                    this.$scope.selected.group = undefined;
                }
            };

            this.$scope.removeEntry = (aclEntryToRemove: Models.AclEntry): void => {
                let foundIndex: number = -1;
                this.$scope.aclEntries.aclEntries.forEach((aclEntry: Models.AclEntry, index: number ) => {
                    if (aclEntry.group === aclEntryToRemove.group)
                        foundIndex = index;
                });
                if (foundIndex >= 0) {
                    this.$scope.aclEntries.aclEntries.splice(foundIndex, 1);
                    this.loadGroups($scope, userGroupService);
                }
            };

            this.$scope.saveCredential = (deviceCredential: Models.IDeviceCredential) => {
                deviceCredentialsService.saveCredential(deviceCredential).then((deviceCredentialSaved: Models.IDeviceCredential) => {
                    deviceCredential.value = deviceCredentialSaved.value;
                    Common.Utils.alertOnSuccess(`Credential '${deviceCredential.name}' saved correctly`);
                    deviceCredential.allowSave = false;
                });
            };

            this.$scope.togglePasswordVisibility = (credential: Models.IDeviceCredential): void => {
                credential.typeHtml = credential.typeHtml === 'password' ? 'text' : 'password';
            };

            this.$scope.toggleSlaveEntry = (masterType: string, aclEntry: Models.AclEntry) => {
                if (masterType === 'write') {
                    aclEntry.read = aclEntry.write ? true : aclEntry.read;
                } else if (masterType === 'gui') {
                    aclEntry.read = aclEntry.canAccessGui ? true : aclEntry.read;
                    aclEntry.write = aclEntry.canAccessGui ? true : aclEntry.write;
                }
            };

        }

        private translateEntries(acls: Models.IAclEntries): void {
            acls.aclEntries.forEach((aclEntry: Models.AclEntry) => {
                this.translateEntry(aclEntry);
            });
        }
        private setBooleans(acls: Models.IAclEntries): void {
            this.$scope.showWrite = acls.aclEntries[0].identityClass !== AclModalController.ALARM_CONFIG_CLASS;
            this.$scope.showGroups = acls.aclEntries[0].identityClass !== AclModalController.ALARM_CONFIG_CLASS;
            this.$scope.showGui = acls.aclEntries[0].canAccessGui !== null;
        }

        private translateEntry(aclEntry: Models.AclEntry): void {
            aclEntry.translatedType = this.translateType(aclEntry.type);
            aclEntry.translatedRole = this.translateRole(aclEntry.role);
        }

        private translateType(type: string): string {
            return this.aclEntryTypeTranslations[type] || this.aclEntryTypeTranslations[type] === ""
                ? this.aclEntryTypeTranslations[type] : type;
        }
        private translateRole(role: string): string {
            return this.aclEntryRoleTranslations[role] ? this.aclEntryRoleTranslations[role] : role;
        }


        private loadGroups($scope: IAclModalScope, userGroupService: Services.UserGroupService): void {
            userGroupService.findAll().then((groups: Models.IUserGroup[]) => {
                this.$scope.groups = groups.filter((group: Models.IUserGroup) => {
                    return !this.$scope.aclEntries.aclEntries.some((aclEntry: Models.AclEntry) => {
                        return '' + aclEntry.group === group.id;
                    });
                });
                if (this.$scope.groups.length > 0) {
                    this.$scope.selected.group = this.$scope.groups[0];
                } else {
                    this.$scope.selected.group = undefined;
                }
            });
        }
    }
}
